/**
 * accountTimezoneViewModel — `(state, raw)` → les props de l'écran « Fuseau horaire ».
 *
 * Deux sources. Le compte (patron A) : `currentUser.object.timezone` et `timezone_auto`, que
 * l'écriture `updateTimezone` remet à jour par le réducteur `currentUser`. La liste des fuseaux
 * (patron B) : `GET api/timezones` n'a pas de case de réducteur — un seul écran la lit, elle ne
 * sort pas de son `use*Data` (`raw`).
 *
 * En mode parent, le compte est celui de l'enfant et l'app n'envoie pas le fuseau du téléphone du
 * parent : le fuseau automatique suit l'appareil DE L'ENFANT. La phrase sous l'interrupteur le dit
 * (`timezoneAutoHint`, qui dit aussi ce que fait le réglage coupé), et c'est la seule différence.
 *
 * Mémoïsé sur les valeurs lues et non sur `currentUser.object`, qui change d'identité à chaque
 * rechargement du compte (même raison que `parentShellViewModel`).
 */
import { createSelector } from "reselect"

import { toCurrentTimezone, toUITimezoneOptions } from "../../../adapters/account/Timezone/timezoneAdapter"
import { accountTimezoneCity } from "../../../../../data/utility/timezones"
import { timezoneAutoHint } from "../../../ui/formatters/timezoneFormatter"
import { type UITimezoneOption } from "../../../ui/types"
import { selectAccount } from "../../shared/displayedStudent"

export type AccountTimezoneViewModel = {
    /** L'identifiant IANA du compte — ce qu'on fige quand on coupe l'automatique sans choisir. */
    timezone: string | null
    /** « Fuseau automatique » — allumé tant que le compte ne dit pas le contraire. */
    auto: boolean
    /** « Oslo » — `null` quand le compte n'a pas de fuseau. */
    currentCity: string | null
    /** « Europe/Oslo », en français quand la liste est chargée (« Asie/Dubaï »). */
    currentLabel: string | null
    autoHint: string
    options: UITimezoneOption[]
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

const selectTimezone = (state: ReduxState): string | null => selectAccount(state)?.timezone || null
// Un backend qui ne sert pas encore le champ : c'est l'automatique, le défaut du réglage.
const selectLabel = (state: ReduxState): string | null => selectAccount(state)?.timezone_label || null
export const selectAuto = (state: ReduxState): boolean => selectAccount(state)?.timezone_auto !== false
const selectParentMode = (state: ReduxState): boolean => state?.data?.family?.isParentMode === true
const selectRaw = (_state: ReduxState, raw: unknown): unknown => raw

const selectOptions = createSelector([selectRaw], (raw) => toUITimezoneOptions(raw))

export const accountTimezoneViewModel = createSelector(
    [selectTimezone, selectLabel, selectAuto, selectParentMode, selectOptions],
    (timezone, label, auto, parentMode, options): AccountTimezoneViewModel => {
        const current = toCurrentTimezone(timezone, options, label)
        return {
            timezone,
            auto,
            currentCity: current?.city ?? null,
            currentLabel: current?.label ?? null,
            autoHint: timezoneAutoHint(parentMode, auto),
            options,
        }
    }
)

/**
 * Ce que lit une ligne d'accès « Fuseau horaire » (les paramètres du parent) : la ville seule,
 * sans la liste. Mémoïsé sur la chaîne du fuseau.
 */
export const accountTimezoneEntryViewModel = createSelector([selectTimezone, selectLabel], (timezone, label) => ({
    cityLabel: accountTimezoneCity(timezone, label),
}))
